/**
 * Gameplay "current game leaderboard" — full-screen standings card that shows
 * during round transitions and between-round cinematics (see
 * scene/scene-visibility.ts for the full list of scenes that surface it).
 *
 * Previous version rendered as a Three.js panel pegged to the right side of
 * the stage; it sat awkwardly under the round-transition scrim and looked
 * nothing like the lobby TOP 10 card. This revision switches to a DOM card
 * that reuses `ensureLobbyLbStyles()` from `ui/lobby-leaderboard.ts` so the
 * visual language (rounded 14px corners, deep-blue background, gold accents,
 * gold/silver/bronze medal rows) is shared across the lobby top-10, the
 * in-game mini-LB (top-5 corner widget), and this full-screen standings card.
 *
 * API shape preserved for `main.ts` + `scene-visibility.ts` compatibility:
 *   - `group` is a stub Three.js Object3D; scene-visibility flips `group.visible`
 *     to toggle the card on/off and a MutationObserver-free polling shim via
 *     `tick()` mirrors that flag onto the DOM card's `hidden` class.
 *   - `setColumnRect(rect)` anchors the card to a camera column when the
 *     streamer enables a camera corner; null reverts to the centered layout.
 *   - `tick()` also drives the score-countup animation the previous version had.
 */

import { Object3D } from 'three';
import type { LbRow } from '@sg/shared';
import { fitText } from '../ui/fit-text.js';
import { ensureLobbyLbStyles } from '../ui/lobby-leaderboard.js';
import { STAGE_HEIGHT, STAGE_WIDTH } from './lib/theme.js';
import type { LeaderboardRect } from './lib/safe-area.js';

export interface LeaderboardHandle {
  /**
   * Stub Object3D retained so the rest of the pipeline can keep toggling
   * `group.visible` / positioning it alongside other scene groups without
   * knowing the underlying renderer changed. Internally the `visible` flag
   * is mirrored onto the DOM card each `tick()`.
   */
  group: Object3D;
  setRows: (rows: LbRow[]) => void;
  setColumnRect: (rect: LeaderboardRect | null) => void;
  tick: (nowMs: number) => void;
  dispose: () => void;
}

// Variant-specific styles layered on top of the shared `.sg-lblb*` base from
// lobby-leaderboard.ts. The full-screen gameplay LB is larger, centered, and
// sports a subtle entry animation so it doesn't pop in under the
// round-transition scrim.
const VARIANT_STYLES = `
  @keyframes sg-gflb-in {
    from { opacity: 0; transform: translate(-50%, calc(-50% + 14px)) scale(0.96); }
    to   { opacity: 1; transform: translate(-50%, -50%) scale(1);                  }
  }
  .sg-lblb[data-role="game-full-lb"] {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 1160px;
    max-width: 62vw;
    height: 760px;
    max-height: 74vh;
    z-index: 45;
    pointer-events: none;
    /* Slightly thicker ring so the card reads at large size. */
    border-width: 3px;
    box-shadow: 0 8px 60px rgba(0,0,0,0.55);
    animation: sg-gflb-in 420ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .sg-lblb[data-role="game-full-lb"].hidden { display: none; }
  .sg-lblb[data-role="game-full-lb"].col-mode {
    /* Camera-column mode: dock to the column rect set via setColumnRect().
       Anchoring is driven from JS by setting inline left/top/width/height —
       the .col-mode class just disables the centered translate so absolute
       coords stick. */
    left: 0; top: 0;
    transform: none;
    max-width: none;
    max-height: none;
    animation: none;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-title {
    font-size: 2.4rem;
    letter-spacing: 0.24em;
    padding: 22px 12px 16px 12px;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-head {
    grid-template-columns: 72px 1fr 140px 110px;
    gap: 12px;
    font-size: 1rem;
    letter-spacing: 0.2em;
    padding: 10px 24px 8px 24px;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-list {
    padding: 10px 0 14px 0;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-row {
    grid-template-columns: 72px 1fr 140px 110px;
    gap: 12px;
    padding: 10px 24px;
    font-size: 1.35rem;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-rank {
    font-size: 1.85rem;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-points {
    font-size: 1.5rem;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-games {
    font-size: 1.1rem;
  }
  .sg-lblb[data-role="game-full-lb"] .sg-lblb-empty {
    padding: 32px 20px;
    font-size: 1.2rem;
  }
  /* Column-mode shrinks everything so it fits in the 480px-wide camera column. */
  .sg-lblb[data-role="game-full-lb"].col-mode .sg-lblb-title { font-size: 1.3rem; padding: 12px 8px 10px 8px; }
  .sg-lblb[data-role="game-full-lb"].col-mode .sg-lblb-head { grid-template-columns: 40px 1fr 80px 54px; font-size: 0.7rem; padding: 6px 10px 4px 10px; }
  .sg-lblb[data-role="game-full-lb"].col-mode .sg-lblb-row { grid-template-columns: 40px 1fr 80px 54px; font-size: 0.95rem; padding: 6px 10px; }
  .sg-lblb[data-role="game-full-lb"].col-mode .sg-lblb-rank { font-size: 1rem; }
  .sg-lblb[data-role="game-full-lb"].col-mode .sg-lblb-points { font-size: 1rem; }
  .sg-lblb[data-role="game-full-lb"].col-mode .sg-lblb-games { font-size: 0.8rem; }
`;

function ensureVariantStyles(): void {
  if (document.getElementById('sg-gflb-variant-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-gflb-variant-style';
  s.textContent = VARIANT_STYLES;
  document.head.appendChild(s);
}

function medalFor(rank: number): string {
  if (rank === 1) return 'sg-r1';
  if (rank === 2) return 'sg-r2';
  if (rank === 3) return 'sg-r3';
  return '';
}

interface RowState {
  playerId: number;
  displayedScore: number;
  targetScore: number;
  animFrom: number;
  animStartMs: number | null;
  rowEl: HTMLDivElement;
  nameEl: HTMLDivElement;
  scoreEl: HTMLDivElement;
  gamesEl: HTMLDivElement;
  rankEl: HTMLDivElement;
}

export function createLeaderboard(stageEl: HTMLElement): LeaderboardHandle {
  ensureLobbyLbStyles();
  ensureVariantStyles();

  const group = new Object3D();
  group.name = 'leaderboard';
  // Default hidden; scene-visibility flips this as scenes change.
  group.visible = false;

  const el = document.createElement('div');
  el.className = 'sg-lblb hidden';
  el.setAttribute('data-role', 'game-full-lb');
  el.innerHTML = `
    <div class="sg-lblb-title">CURRENT GAME LEADERBOARD</div>
    <div class="sg-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div><div>WON</div></div>
    <div class="sg-lblb-body">
      <div class="sg-lblb-list" data-role="rows">
        <div class="sg-lblb-empty">No scores yet — first clue coming up!</div>
      </div>
    </div>
  `;
  stageEl.appendChild(el);
  const rowsContainer = el.querySelector<HTMLDivElement>('[data-role="rows"]')!;

  const rowStates = new Map<number, RowState>();
  let isEmpty = true;
  // Column-mode inline geometry — preserved so `tick()` doesn't stomp it.
  let columnInline: { left: number; top: number; width: number; height: number } | null = null;
  // Mirror group.visible onto the DOM's `hidden` class. We sample per-tick so
  // scene-visibility's plain assignment (`group.visible = ...`) stays the one
  // source of truth — no MutationObserver / proxies needed.
  let lastVisible = false;

  function buildRow(r: LbRow): RowState {
    const rowEl = document.createElement('div');
    rowEl.className = `sg-lblb-row ${medalFor(r.rank)}`.trim();
    rowEl.innerHTML = `
      <div class="sg-lblb-rank">${r.rank}</div>
      <div class="sg-lblb-name" data-role="name"></div>
      <div class="sg-lblb-points" data-role="score">${r.total_points.toLocaleString()}</div>
      <div class="sg-lblb-games" data-role="games">${r.games_won ?? 0}</div>
    `;
    const rankEl = rowEl.querySelector<HTMLDivElement>('.sg-lblb-rank')!;
    const nameEl = rowEl.querySelector<HTMLDivElement>('[data-role="name"]')!;
    const scoreEl = rowEl.querySelector<HTMLDivElement>('[data-role="score"]')!;
    const gamesEl = rowEl.querySelector<HTMLDivElement>('[data-role="games"]')!;
    nameEl.textContent = r.display_name;
    // fitText once the row lands in the DOM so the laid-out width is real.
    requestAnimationFrame(() => fitText(nameEl, { min: 14, max: 22 }));
    return {
      playerId: r.player_id,
      displayedScore: r.total_points,
      targetScore: r.total_points,
      animFrom: r.total_points,
      animStartMs: null,
      rowEl,
      nameEl,
      scoreEl,
      gamesEl,
      rankEl,
    };
  }

  function setRows(rows: LbRow[]): void {
    const top10 = rows.slice(0, 10);
    if (top10.length === 0) {
      if (!isEmpty) {
        rowsContainer.innerHTML = '<div class="sg-lblb-empty">No scores yet — first clue coming up!</div>';
        for (const s of rowStates.values()) s.rowEl.remove();
        rowStates.clear();
        isEmpty = true;
      }
      return;
    }
    if (isEmpty) {
      rowsContainer.innerHTML = '';
      isEmpty = false;
    }
    // Rebuild the ordered list in one swap — ties + score swaps naturally
    // reorder DOM without flicker because existing row nodes are reused via
    // the rowStates map (count-up animations in `tick()` keep running).
    const seen = new Set<number>();
    const fragment = document.createDocumentFragment();
    for (const r of top10) {
      seen.add(r.player_id);
      let state = rowStates.get(r.player_id);
      if (!state) {
        state = buildRow(r);
        rowStates.set(r.player_id, state);
      } else {
        const desiredCls = `sg-lblb-row ${medalFor(r.rank)}`.trim();
        if (state.rowEl.className !== desiredCls) state.rowEl.className = desiredCls;
        if (state.rankEl.textContent !== String(r.rank)) state.rankEl.textContent = String(r.rank);
        if (state.nameEl.textContent !== r.display_name) {
          state.nameEl.textContent = r.display_name;
          const nameEl = state.nameEl;
          requestAnimationFrame(() => fitText(nameEl, { min: 14, max: 22 }));
        }
        const gamesStr = String(r.games_won ?? 0);
        if (state.gamesEl.textContent !== gamesStr) state.gamesEl.textContent = gamesStr;
      }
      fragment.appendChild(state.rowEl);
      if (state.targetScore !== r.total_points) {
        state.animFrom = state.displayedScore;
        state.targetScore = r.total_points;
        state.animStartMs = performance.now();
      }
    }
    rowsContainer.replaceChildren(fragment);
    for (const [pid, s] of rowStates) {
      if (!seen.has(pid)) {
        s.rowEl.remove();
        rowStates.delete(pid);
      }
    }
  }

  function setColumnRect(rect: LeaderboardRect | null): void {
    if (!rect) {
      columnInline = null;
      el.classList.remove('col-mode');
      // Clear the inline geometry so the CSS-centered layout takes over again.
      el.style.left = '';
      el.style.top = '';
      el.style.width = '';
      el.style.height = '';
      return;
    }
    // Stage-space coords use origin=center, +y up. Convert to DOM coords
    // (origin top-left, +y down). x shift: +STAGE_W/2. y shift: STAGE_H/2 - y.
    const left = rect.x + STAGE_WIDTH / 2;
    const top = STAGE_HEIGHT / 2 - rect.y;
    columnInline = { left, top, width: rect.width, height: rect.height };
    el.classList.add('col-mode');
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
  }

  function tick(nowMs: number): void {
    // Mirror group.visible -> DOM hidden class. Doing it here (rather than via
    // a setter proxy) keeps scene-visibility's direct assignment semantics.
    if (group.visible !== lastVisible) {
      lastVisible = group.visible;
      el.classList.toggle('hidden', !group.visible);
      // Re-trigger the entry animation when the card comes back onto stage
      // (the scrim behind the round-transition hides it during board, so the
      // next `group.visible = true` should feel like a fresh entry).
      if (group.visible && !columnInline) {
        el.style.animation = 'none';
        void el.offsetHeight; // force reflow so the anim can restart
        el.style.animation = '';
      }
    }
    // Score count-up animation. 600ms per score change.
    for (const s of rowStates.values()) {
      if (s.animStartMs === null) continue;
      const t = Math.min(1, (nowMs - s.animStartMs) / 600);
      const e = 1 - Math.pow(1 - t, 3);
      const val = Math.round(s.animFrom + (s.targetScore - s.animFrom) * e);
      if (val !== s.displayedScore) {
        s.displayedScore = val;
        s.scoreEl.textContent = val.toLocaleString();
      }
      if (t >= 1) {
        s.displayedScore = s.targetScore;
        s.scoreEl.textContent = s.targetScore.toLocaleString();
        s.animStartMs = null;
      }
    }
  }

  return {
    group,
    setRows,
    setColumnRect,
    tick,
    dispose() {
      el.remove();
      rowStates.clear();
    },
  };
}
